/**
 * tagNodeUtils.js
 *
 * Utilities for managing the "facade tagNode" objects used in the onboarding
 * wizard steps that handle tag selections (subjects, expertise, etc.).
 *
 * Background — why the shape is odd:
 *
 * The legacy lore mutators (tagNode.create / updateDynamic / destroyDynamic) do
 * not operate on plain JS objects. They expect a specific envelope shape that
 * mirrors what the API returns, where the payload is nested under `data` and
 * related models are attached as `_tag` / `_tagNode` side-loads.
 *
 * To avoid hitting the real API for every intermediate UI interaction, the wizard
 * steps maintain a local "facade" list of these envelope objects. New selections
 * that haven't been persisted yet are given an `id` prefixed with "new-" so the
 * save logic can tell them apart from real DB records:
 *
 *   id starts with "new-"  →  needs a CREATE call on save
 *   id is a real number    →  needs an UPDATE or DELETE call on save
 *
 * After a successful save the soft-delete list must be "reset" — any items that
 * just had their real DB row deleted are converted back to the "new-" shape so
 * that clicking Next a second time doesn't try to delete them again (they no
 * longer exist in the DB). We preserve the description so the item looks correct
 * if the user re-selects it before moving on.
 */

/**
 * Create a new unsaved facade tagNode for a tag the user just selected.
 *
 * The "new-" prefix on the id is the convention that tells the save logic this
 * item hasn't been persisted yet and needs a CREATE call rather than an UPDATE
 * or DELETE. The description starts empty and can be filled in via the details
 * dialog before saving.
 *
 * @param {object} tag - The tag payload (must have an `id` field)
 * @returns {object} A facade tagNode in the new- shape
 */
export function createFacadeTagNode(tag) {
  return {
    dirty: true,
    id: `new-${tag.id}`,
    _tag: {
      id: tag.id,
    },
    _tagNode: {
      id: `new-${tag.id}`,
      data: {
        tagId: tag.id,
        description: '',
      },
    },
  };
}

/**
 * Return a new list where the facade tagNode matching `tagId` has its
 * description updated. All other items are returned unchanged.
 *
 * The description is buried inside `_tagNode.data` to match the envelope shape
 * the legacy mutators expect — this utility hides that nesting from callers.
 *
 * @param {Array}  items       - The current facade tagNode list
 * @param {*}      tagId       - The `_tag.id` of the item to update
 * @param {string} description - The new description value
 * @returns {Array} A new array with the matching item's description updated
 */
export function updateFacadeTagNodeDescription(items, tagId, description) {
  return items.map((item) => {
    if (item._tag.id !== tagId) return item;
    return {
      ...item,
      dirty: true,
      _tagNode: {
        ...item._tagNode,
        data: {
          ...item._tagNode.data,
          description,
        },
      },
    };
  });
}

/**
 * Toggle a tag's selection state, keeping the active list and soft-delete list
 * in sync.
 *
 * - If the tag is currently selected (in `items`), it is moved to `softDeletes`.
 * - If the tag is not selected, it is restored from `softDeletes` if it was
 *   previously deselected (preserving its description), or a fresh facade is
 *   created if it has never been selected before.
 *
 * Returns new copies of both lists; does not mutate the originals.
 *
 * @param {Array}  items       - The current selected facade tagNode list
 * @param {Array}  softDeletes - The current soft-delete list
 * @param {object} tag         - The tag being toggled (must have an `id` field)
 * @returns {{ items: Array, softDeletes: Array }}
 */
export function toggleFacadeTagNode(items, softDeletes, tag) {
  const selected = items.find((item) => item._tag.id === tag.id);

  if (selected) {
    // Deselect: move out of the active list into soft-deletes
    return {
      items: items.filter((s) => s._tag.id !== tag.id),
      softDeletes: [...softDeletes.filter((s) => s._tag.id !== tag.id), selected],
    };
  } else {
    // Select: restore from soft-deletes (keeps description) or create fresh
    const restored = softDeletes.find((s) => s._tag.id === tag.id);
    const item = restored ?? createFacadeTagNode(tag);
    return {
      items: [...items.filter((s) => s._tag.id !== tag.id), item],
      softDeletes: softDeletes.filter((s) => s._tag.id !== tag.id),
    };
  }
}

/**
 * Persist all pending changes in a facade tagNode list to the API.
 *
 * Partitions the list into three buckets and fires the appropriate mutation
 * for each:
 *   - new- items that are dirty  → CREATE
 *   - existing items that dirty  → UPDATE
 *   - soft-deleted DB records    → DELETE
 *
 * Returns the updated soft-delete list (with deleted DB records reset to the
 * new- shape) so the caller can store it and avoid double-deletion on a
 * subsequent save.
 *
 * @param {object} options
 * @param {Array}    options.items          - The current selected facade list
 * @param {Array}    options.softDeletes    - The current soft-delete list
 * @param {*}        options.userId         - The user id for CREATE calls
 * @param {Function} options.tagNodeCreate  - Mutation function for creates
 * @param {Function} options.tagNodeUpdate  - Mutation function for updates
 * @param {Function} options.tagNodeDestroy - Mutation function for deletes
 * @returns {Promise<Array>} The updated soft-delete list after reset
 */
export async function saveFacadeTagNodes({ items, softDeletes, userId, tagNodeCreate, tagNodeUpdate, tagNodeDestroy }) {
  const toCreate = items.filter((item) => item.dirty && String(item.id).startsWith('new-'));
  const toUpdate = items.filter((item) => item.dirty && !String(item.id).startsWith('new-'));
  const toDelete = softDeletes.filter((sd) => !String(sd.id).startsWith('new-'));

  const createPromises = toCreate.map((item) => tagNodeCreate({
    tagId: item._tag.id,
    description: item._tagNode.data.description,
    userId,
  }));

  const updatePromises = toUpdate.map((item) => tagNodeUpdate({
    id: item.id,
    data: { id: item.id, description: item._tagNode.data.description },
  }));

  const deletePromises = toDelete.map((sd) => tagNodeDestroy({
    id: sd.id,
    data: { id: sd.id },
  }));

  await Promise.all([...createPromises, ...updatePromises, ...deletePromises]);

  return resetDeletedTagNodes(softDeletes);
}

/**
 * After successfully deleting soft-deleted tagNodes from the DB, convert any
 * that had real DB IDs back to the "new-" facade shape.
 *
 * This prevents double-deletion: the next time the user clicks Next, items in
 * the soft-delete list that start with "new-" are skipped by the delete logic.
 *
 * @param {Array} softDeletes - The current soft-delete list
 * @returns {Array} A new array with persisted items reset to the new- shape
 */
export function resetDeletedTagNodes(softDeletes) {
  return softDeletes.map((sd) => {
    // Already a "new-" item — was never persisted, nothing to reset
    if (String(sd.id).startsWith('new-')) {
      return sd;
    }

    // This item had a real DB ID and was just deleted. Reset it to the "new-"
    // shape so a subsequent Next click won't attempt to delete it again.
    // We keep the description so the UI stays consistent if the user re-selects.
    return {
      dirty: true,
      id: `new-${sd._tag.id}`,
      _tag: {
        id: sd._tag.id,
      },
      _tagNode: {
        id: `new-${sd._tag.id}`,
        data: {
          tagId: sd._tag.id,
          description: sd._tagNode.data.description,
        },
      },
    };
  });
}
